import { Controller } from '@hotwired/stimulus'
import { get, patch, destroy } from '@rails/request.js'
import { showConfirmModal } from '../lib/custom_confirm'
import { setHidden } from '../lib/display_toggle'
import { isSoftphoneSurface } from '../lib/softphone_focus_guard'

// Navbar notifications dropdown — opens an in-place panel from the bell icon
// instead of navigating to a full-page inbox. Fetches recent notifications
// from /messages/dropdown.json, partitions them into Announcement/Alert tabs, and
// keeps the bell's unread badge in sync after mark-read actions.
//
// Targets:
//   button — the bell <button>/<a> trigger(s); the desktop and mobile bells
//            share ONE controller instance (scoped to the navbar container)
//   panel  — the dropdown panel container (toggled via [hidden])
//   list   — the <ul> that receives rendered notification items
//   empty  — the empty-state element shown when the active tab has no items
//   tab    — one per tab button (Announcement / Alert)
//   badge  — optional unread-count badge element(s), one per bell, all kept in sync
//
// Values:
//   indexUrl  — GET /messages/dropdown.json (returns { unread_count, items })
//   markAllUrl       — PATCH /messages/mark_all_read
//   markAllUnreadUrl — PATCH /messages/mark_all_unread
//   deleteAllUrl     — DELETE /messages/delete_all
//
// All three mutation URLs are exposed as Stimulus values so the host
// view can override paths without forking the controller; the `.json`
// suffix and ?tab= query string are appended at call-time.
// The mobile certification probe's focusable set, excluding disabled controls
// so the keyboard trap only visits usable controls.
const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([type=hidden]):not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

// Mirrors SameOriginPath (app/lib/same_origin_path.rb): a leading single
// slash, and no scheme, backslash, TAB/LF/CR (WHATWG strips them, so
// "/<TAB>/evil.com" resolves to //evil.com) or NUL anywhere.
const UNSAFE_PATH_CONTENT = /:\/\/|\\|[\t\r\n\0]/
const isSameOriginPath = (path) =>
  (path === '/' || /^\/[^/\\]/.test(path)) && !UNSAFE_PATH_CONTENT.test(path)

export default class extends Controller {
  static targets = ['button', 'panel', 'backdrop', 'close', 'list', 'empty', 'tab', 'badge']
  static values = {
    indexUrl: { type: String, default: '/messages/dropdown.json' },
    markAllUrl: { type: String, default: '/messages/mark_all_read' },
    markAllUnreadUrl: { type: String, default: '/messages/mark_all_unread' },
    deleteAllUrl: { type: String, default: '/messages/delete_all' },
    userTimeZone: { type: String, default: 'America/New_York' },
  }

  connect() {
    this.items = []
    this.activeTab = 'announcement'
    this.defaultEmptyHtml = this.hasEmptyTarget ? this.emptyTarget.innerHTML : ''
    this.escapeHandler = null
    this.trapHandler = null
    this.opener = null
    this.confirmationPending = false
  }

  disconnect() {
    this.removeEscapeHandler()
    this.removeFocusTrap()
    // A pending confirmation still owns its Bootstrap scroll lock; its
    // teardown will restore the unlocked page even though this drawer is gone.
    if (!this.confirmationPending) document.body.style.overflow = ''
    this.confirmationPending = false
  }

  toggle(event) {
    event?.preventDefault()
    event?.stopPropagation()
    if (this.isOpen()) {
      this.close()
    } else {
      this.open(event?.currentTarget)
    }
  }

  isOpen() {
    return this.hasPanelTarget && !this.panelTarget.hidden
  }

  open(trigger) {
    if (!this.hasPanelTarget) return
    // The panel is a modal dialog (role=dialog, aria-modal): remember the
    // opener, move focus inside, and keep Tab inside while it is open —
    // WCAG 2.4.3 / A11Y-2 (PE-050 a). Without this the page's own first
    // control, hidden under the fixed header at phone width, is the next Tab
    // stop.
    // Safari taps do not focus buttons. Remember the actual bell rather
    // than whichever page element happened to have focus before the tap.
    const opener = this.buttonTargets.includes(trigger) ? trigger : document.activeElement
    this.opener = opener instanceof HTMLElement ? opener : null
    this.panelTarget.hidden = false
    if (this.hasBackdropTarget) this.backdropTarget.hidden = false
    document.body.style.overflow = 'hidden'
    this.bindEscapeHandler()
    this.bindFocusTrap()
    this.focusInitialControl()
    this.loadItems()
  }

  close() {
    if (!this.hasPanelTarget) return
    this.confirmationPending = false
    this.panelTarget.hidden = true
    if (this.hasBackdropTarget) this.backdropTarget.hidden = true
    document.body.style.overflow = ''
    this.removeEscapeHandler()
    this.removeFocusTrap()
    this.restoreOpenerFocus()
  }

  // Initial focus lands on the Close control — the one action every visitor
  // wants first and the first control in the dialog's own order — falling back
  // to the panel itself (tabindex=-1) when the markup carries no close target.
  focusInitialControl() {
    const target = this.hasCloseTarget ? this.closeTarget : this.panelTarget
    target.focus()
  }

  restoreOpenerFocus() {
    const opener = this.opener
    this.opener = null
    if (opener && opener.isConnected) opener.focus()
  }

  // Hidden state is read from the `hidden` attribute (this panel toggles its
  // sections that way), not from layout — jsdom reports no layout at all.
  tabbables() {
    return Array.from(this.panelTarget.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (el) => !el.closest('[hidden]'),
    )
  }

  // Cycle Tab / Shift+Tab within the dialog (WCAG 2.1.2). Focus resting on the
  // panel container itself counts as "before the first control".
  handleTrap(event) {
    if (event.key !== 'Tab' || !this.isOpen()) return
    // #15710: during a call the GOAT Connect dialer paints above this panel;
    // Tab between its own fields (call note, transfer search) stays there.
    if (isSoftphoneSurface(document.activeElement)) return
    const tabbables = this.tabbables()
    if (tabbables.length === 0) {
      event.preventDefault()
      this.panelTarget.focus()
      return
    }
    const first = tabbables[0]
    const last = tabbables[tabbables.length - 1]
    const active = document.activeElement
    const outside = !this.panelTarget.contains(active)
    if (event.shiftKey && (active === first || active === this.panelTarget || outside)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && (active === last || active === this.panelTarget || outside)) {
      event.preventDefault()
      first.focus()
    }
  }

  bindFocusTrap() {
    if (this.trapHandler) return
    this.trapHandler = (event) => this.handleTrap(event)
    document.addEventListener('keydown', this.trapHandler)
  }

  removeFocusTrap() {
    if (!this.trapHandler) return
    document.removeEventListener('keydown', this.trapHandler)
    this.trapHandler = null
  }

  handleEscape(event) {
    if (event.key !== 'Escape' || !this.isOpen()) return
    // #15710: Escape inside the GOAT Connect dialer (clearing the dial field)
    // belongs to the dialer, not to this panel underneath it.
    if (isSoftphoneSurface(document.activeElement)) return
    this.close()
  }

  bindEscapeHandler() {
    if (this.escapeHandler) return

    this.escapeHandler = (event) => this.handleEscape(event)
    document.addEventListener('keydown', this.escapeHandler)
  }

  removeEscapeHandler() {
    if (!this.escapeHandler) return

    document.removeEventListener('keydown', this.escapeHandler)
    this.escapeHandler = null
  }

  async loadItems() {
    try {
      const response = await get(this.indexUrlValue, { responseKind: 'json' })
      if (!response.ok) {
        this.renderEmpty('Failed to load notifications.')
        return
      }
      const data = await response.json
      this.items = Array.isArray(data.items) ? data.items : []
      this.updateBadge(data.unread_count ?? 0)
      this.render()
    } catch (e) {
      console.error('[notifications-dropdown] load failed', e)
      this.renderEmpty('Failed to load notifications.')
    }
  }

  switchTab(event) {
    event.preventDefault()
    this.selectTab(event.currentTarget.dataset.tab)
  }

  selectTab(tab) {
    if (!tab || tab === this.activeTab) return
    if (!this.tabTargets.some((el) => el.dataset.tab === tab)) return
    this.activeTab = tab
    this.tabTargets.forEach((el) => el.classList.toggle('active', el.dataset.tab === tab))
    this.render()
  }

  // "Message from GOAT" toast → Read It (#16746): the toast dispatches
  // `notifications-dropdown:open` on window with { tab } once the message is
  // marked read, and the panel opens on that message's tab. Wired by the
  // navbar container's data-action (layouts/goatleads/_top_nav_bar).
  openFromEvent(event) {
    this.selectTab(event?.detail?.tab)
    if (!this.isOpen()) this.open()
  }

  // The three bulk buttons act on the CURRENTLY ACTIVE tab only — sending
  // ?tab=announcement or ?tab=alert so the backend filters by message_type.
  // Switching tabs and clicking a bulk button never touches the other tab.

  async markAllRead(event) {
    event?.preventDefault()
    const tab = this.activeTab
    try {
      const response = await patch(`${this.markAllUrlValue}.json?tab=${tab}`, { responseKind: 'json' })
      if (response.ok) {
        const data = await this.responseJson(response)
        const now = new Date().toISOString()
        this.items = this.items.map((i) => (i.tab === tab ? { ...i, read_at: i.read_at ?? now } : i))
        this.updateBadge(data.unread_count ?? this.items.filter((i) => !i.read_at).length)
        this.render()
      }
    } catch (e) {
      console.error('[notifications-dropdown] mark-all-read failed', e)
    }
  }

  async markAllUnread(event) {
    event?.preventDefault()
    const tab = this.activeTab
    try {
      const response = await patch(`${this.markAllUnreadUrlValue}.json?tab=${tab}`, { responseKind: 'json' })
      if (response.ok) {
        const data = await this.responseJson(response)
        this.items = this.items.map((i) => (i.tab === tab ? { ...i, read_at: null } : i))
        this.updateBadge(data.unread_count ?? this.items.filter((i) => !i.read_at).length)
        this.render()
      }
    } catch (e) {
      console.error('[notifications-dropdown] mark-all-unread failed', e)
    }
  }

  async deleteAll(event) {
    event?.preventDefault()
    const tab = this.activeTab
    const confirmMessage = tab === 'alert' ? 'Delete all your alerts?' : 'Delete all your announcement notifications?'
    const confirmed = await this.confirmDeletion(confirmMessage, {
      variant: 'danger', title: 'Delete notifications', confirmText: 'Delete All'
    }, event?.currentTarget)
    if (!confirmed) return

    try {
      const response = await destroy(`${this.deleteAllUrlValue}.json?tab=${tab}`, { responseKind: 'json' })
      if (response.ok) {
        const data = await this.responseJson(response)
        this.items = this.items.filter((i) => i.tab !== tab)
        this.updateBadge(data.unread_count ?? this.items.filter((i) => !i.read_at).length)
        this.render()
      }
    } catch (e) {
      console.error('[notifications-dropdown] delete-all failed', e)
    }
  }

  // Toggle a single notification's read state via the per-row icon button.
  // Reads the current state from data-current-read on the <li> and flips it.
  async toggleItemReadButton(event) {
    event.preventDefault()
    event.stopPropagation()
    const li = event.currentTarget.closest('[data-notification-id]')
    if (!li) return
    const id = li.dataset.notificationId
    const currentlyRead = li.dataset.currentRead === 'true'
    const markRead = !currentlyRead
    try {
      const response = await patch(`/messages/${id}.json?read=${markRead}`, { responseKind: 'json' })
      if (response.ok) {
        const data = await this.responseJson(response)
        const idx = this.items.findIndex((i) => String(i.id) === String(id))
        if (idx !== -1) {
          this.items[idx] = {
            ...this.items[idx],
            read_at: markRead ? new Date().toISOString() : null,
          }
        }
        this.updateBadge(data.unread_count ?? this.items.filter((i) => !i.read_at).length)
        this.render()
      }
    } catch (e) {
      console.error('[notifications-dropdown] toggle-read failed', e)
    }
  }

  async deleteItem(event) {
    event.preventDefault()
    event.stopPropagation()
    const li = event.currentTarget.closest('[data-notification-id]')
    if (!li) return
    const id = li.dataset.notificationId

    // Confirm via the app's project-standard styled modal helper.
    const confirmed = await this.confirmDeletion('Delete this notification?', {
      variant: 'danger', title: 'Delete notification', confirmText: 'Delete'
    }, event.currentTarget)
    if (!confirmed) return

    try {
      const response = await destroy(`/messages/${id}.json`, { responseKind: 'json' })
      if (response.ok) {
        const data = await this.responseJson(response)
        this.items = this.items.filter((i) => String(i.id) !== String(id))
        this.updateBadge(data.unread_count ?? this.items.filter((i) => !i.read_at).length)
        const focusWasInList = this.hasListTarget && this.listTarget.contains(document.activeElement)
        this.render()
        if (focusWasInList && this.isOpen()) this.focusInitialControl()
      }
    } catch (e) {
      console.error('[notifications-dropdown] delete failed', e)
    }
  }

  // This drawer sits above the shared modal layer. Temporarily hide it and
  // its backdrop so confirmation remains visible and owns keyboard focus.
  // Restore the parent only after Bootstrap finishes its teardown.
  async confirmDeletion(message, options, trigger) {
    this.confirmationPending = true
    this.removeEscapeHandler()
    this.removeFocusTrap()
    this.panelTarget.hidden = true
    if (this.hasBackdropTarget) this.backdropTarget.hidden = true
    // Bootstrap saves and restores the current overflow value. Hand it the
    // page's unlocked state so a disconnected drawer cannot leave it locked.
    document.body.style.overflow = ''
    try {
      return await showConfirmModal(message, options)
    } finally {
      const restorePanel = this.confirmationPending
      this.confirmationPending = false
      if (restorePanel && this.element.isConnected) {
        this.panelTarget.hidden = false
        if (this.hasBackdropTarget) this.backdropTarget.hidden = false
        document.body.style.overflow = 'hidden'
        this.bindEscapeHandler()
        this.bindFocusTrap()
        if (trigger?.isConnected) trigger.focus()
        else this.focusInitialControl()
      }
    }
  }

  // One controller instance serves BOTH navbar bells (desktop + mobile), each
  // carrying its own badge target — keep every badge in sync.
  updateBadge(count) {
    if (!this.hasBadgeTarget) return
    this.badgeTargets.forEach((badge) => {
      badge.textContent = String(count)
      setHidden(badge, !(count > 0))
    })
  }

  async responseJson(response) {
    if (!response.json) return {}
    if (typeof response.json === 'function') return response.json()
    return response.json
  }

  render() {
    if (!this.hasListTarget) return
    const filtered = this.items.filter((i) => i.tab === this.activeTab)
    if (filtered.length === 0) {
      this.listTarget.innerHTML = ''
      this.renderEmpty()
      return
    }
    if (this.hasEmptyTarget) this.emptyTarget.hidden = true
    this.listTarget.innerHTML = filtered.map((i) => this.renderItem(i)).join('')
  }

  renderEmpty(messageOverride) {
    if (!this.hasEmptyTarget) return
    this.emptyTarget.hidden = false
    if (messageOverride) {
      this.emptyTarget.innerHTML = '<i class="ph ph-bell-slash gl-notifications-panel-empty-icon"></i>' +
        `<p class="gl-notifications-panel-empty-text">${this.escapeHtml(messageOverride)}</p>`
    } else {
      this.emptyTarget.innerHTML = this.defaultEmptyHtml
    }
  }

  renderItem(item) {
    const isUnread = !item.read_at
    const toggleTitle = isUnread ? 'Mark as read' : 'Mark as unread'
    const toggleIcon = isUnread ? 'ph-check' : 'ph-envelope-open'
    return `
      <li class="gl-notifications-item ${isUnread ? 'gl-notifications-item--unread' : ''}" data-notification-id="${item.id}" data-current-read="${!isUnread}">
        <label class="gl-notifications-item-checkbox" title="Select">
          <input type="checkbox" data-action="change->notifications-dropdown#toggleSelected" />
        </label>
        <span class="gl-notifications-item-dot ${isUnread ? '' : 'invisible'}" aria-hidden="true"></span>
        <span class="gl-notifications-item-icon"><i class="ph-fill ph-chat-circle-text"></i></span>
        <div class="gl-notifications-item-body">
          <div class="gl-notifications-item-meta">
            <span class="gl-notifications-item-type">${this.escapeHtml(item.type_label)}</span>
            <span class="gl-notifications-item-time">${this.relativeTime(item.created_at)}</span>
          </div>
          <div class="gl-notifications-item-subject">${this.renderSubject(item)}</div>
          <div class="gl-notifications-item-message">${this.escapeHtml(item.message || '')}</div>
          ${this.renderActions(item)}
        </div>
        <div class="gl-notifications-item-actions">
          <button type="button" class="gl-notifications-item-mark" data-action="click->notifications-dropdown#toggleItemReadButton" data-gl-tooltip="${toggleTitle}" aria-label="${toggleTitle}"><i class="ph ${toggleIcon}" aria-hidden="true"></i></button>
          <button type="button" class="gl-notifications-item-delete" data-action="click->notifications-dropdown#deleteItem" data-gl-tooltip="Delete notification" aria-label="Delete notification"><i class="ph ph-trash" aria-hidden="true"></i></button>
        </div>
      </li>
    `
  }

  // An entry with a link_path (e.g. a missed call's lead, #16217) opens it
  // from the subject. The server sends only same-origin paths; the check is
  // repeated here (mirroring SameOriginPath) so an unsafe path renders as text.
  renderSubject(item) {
    const subject = this.escapeHtml(item.subject || '(no subject)')
    const path = typeof item.link_path === 'string' ? item.link_path : ''
    if (!isSameOriginPath(path)) return subject
    return `<a class="gl-notifications-item-link" href="${this.escapeHtml(path)}">${subject}</a>`
  }

  // Buttons under an entry (e.g. Texts Not Sending's Fix and Open Needs
  // Attention, #20729): the primary one solid, the rest outline (BUTTON-1/2).
  // Same-origin paths only, as for the subject link.
  renderActions(item) {
    const actions = Array.isArray(item.actions) ? item.actions : []
    const links = actions
      .filter((action) => action && typeof action.label === 'string' && isSameOriginPath(action.path))
      .map((action) => {
        const tier = action.primary ? 'gl-btn-primary' : 'gl-btn-outline'
        return `<a class="gl-btn ${tier} gl-btn-sm" href="${this.escapeHtml(action.path)}">${this.escapeHtml(action.label)}</a>`
      })
    if (links.length === 0) return ''
    return `<div class="d-flex flex-wrap gap-2 mt-2" data-notification-actions>${links.join('')}</div>`
  }

  escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[char])
  }

  // Checkbox is selection-only (not state). Toggles a CSS class for
  // styling; future bulk-action buttons in the panel can query
  // [data-notification-id].selected to know which rows the user picked.
  toggleSelected(event) {
    event.stopPropagation()
    const li = event.currentTarget.closest('[data-notification-id]')
    if (!li) return
    li.classList.toggle('selected', Boolean(event.currentTarget.checked))
  }

  relativeTime(iso) {
    if (!iso) return ''
    const then = new Date(iso).getTime()
    if (Number.isNaN(then)) return ''
    const diffSec = Math.floor((Date.now() - then) / 1000)
    if (diffSec < 60) return 'just now'
    const diffMin = Math.floor(diffSec / 60)
    if (diffMin < 60) return `${diffMin} min${diffMin === 1 ? '' : 's'} ago`
    const diffHr = Math.floor(diffMin / 60)
    if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`
    const diffDay = Math.floor(diffHr / 24)
    if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`
    return new Date(iso).toLocaleDateString(undefined, { timeZone: this.userTimeZoneValue })
  }
}
